import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { hubApi, fetchMe, type Me } from '../../lib/hubApi';
import { useHubStore, type HubFinding } from '../../store/useHubStore';
import { HubPageHeader } from '../../components/hub/HubPageHeader';

export function HubSettingsPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const { findings, loadFindings, setStatus } = useHubStore();

  useEffect(() => {
    void fetchMe().then((data) => {
      setMe(data);
      setNotifyEmail(data?.email ?? '');
    });
    if (findings.length === 0) void loadFindings();
  }, [findings.length, loadFindings]);

  const dismissed = useMemo(
    () => findings.filter((f) => f.status === 'dismissed'),
    [findings],
  );
  const snoozed = useMemo(() => findings.filter((f) => f.status === 'snoozed'), [findings]);

  const saveEmail = async () => {
    // The notify-email endpoint is a Day-2 follow-up. If it 404s, surface
    // the Day-2 state honestly rather than letting the user wonder.
    try {
      await hubApi.post('/settings/notify-email', { email: notifyEmail });
      toast.success('Notification email saved');
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        toast('Notification preferences land in Day 2');
      } else {
        const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
        toast.error(m ?? (err as Error).message);
      }
    }
  };

  const undoStatus = async (f: HubFinding, label: string) => {
    try {
      await setStatus(f.id, 'open');
      toast.success(`${label} ${f.title.slice(0, 60)}${f.title.length > 60 ? '…' : ''}`);
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
    }
  };

  return (
    <section className="space-y-6 max-w-3xl">
      <HubPageHeader title="Settings" />

      <section className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-3 text-sm">
        <h2 className="font-semibold text-text-strong">Profile</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-xs">
          <dt className="text-text-muted">Salesforce username</dt>
          <dd className="text-text">{me?.username ?? '-'}</dd>
          <dt className="text-text-muted">Salesforce user ID</dt>
          <dd className="font-mono text-text">{me?.sfUserId ?? '-'}</dd>
          <dt className="text-text-muted">Org ID</dt>
          <dd className="font-mono text-text">{me?.sfOrgId ?? '-'}</dd>
        </dl>
      </section>

      <section className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-3 text-sm">
        <h2 className="font-semibold text-text-strong">Notifications</h2>
        <p className="text-xs text-text-muted">
          Email used for OrgIQ Hub confirmations (TYRL request receipts, etc.).
        </p>
        <div className="flex gap-2 items-center">
          <input
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            className="flex-1 px-3 py-2 rounded-md border border-border bg-surface-1 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
          />
          <button
            type="button"
            onClick={saveEmail}
            className="px-3.5 py-2 rounded-md border border-border bg-surface-1 text-text text-sm hover:bg-surface-hover hover:border-border-strong transition-colors"
          >
            Save
          </button>
        </div>
        <p className="mt-2 text-xs text-text-muted">
          Alert subscriptions for findings live on a separate page:{' '}
          <Link to="/hub/settings/alerts" className="text-sfblue hover:underline">
            manage alerts →
          </Link>
        </p>
      </section>

      <DismissedSection
        title="Snoozed findings"
        findings={snoozed}
        emptyText="No snoozed findings."
        onUndo={(f) => undoStatus(f, 'Unsnoozed')}
        ctaLabel="Unsnooze"
      />

      <DismissedSection
        title="Dismissed findings"
        findings={dismissed}
        emptyText="No dismissed findings."
        onUndo={(f) => undoStatus(f, 'Restored')}
        ctaLabel="Restore"
      />
    </section>
  );
}

function DismissedSection({
  title,
  findings,
  emptyText,
  onUndo,
  ctaLabel,
}: {
  title: string;
  findings: HubFinding[];
  emptyText: string;
  onUndo: (f: HubFinding) => void;
  ctaLabel: string;
}) {
  return (
    <section className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-3 text-sm">
      <h2 className="font-semibold text-text-strong">{title}</h2>
      {findings.length === 0 ? (
        <p className="text-xs text-text-muted">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-border-subtle text-xs">
          {findings.map((f) => (
            <li key={f.id} className="py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="truncate text-text">{f.title}</div>
                <div className="text-text-muted mt-0.5">{f.category}</div>
              </div>
              <button
                type="button"
                onClick={() => onUndo(f)}
                className="px-2.5 py-1 rounded-md border border-border bg-surface-1 text-text hover:bg-surface-hover hover:border-border-strong transition-colors"
              >
                {ctaLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
